import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScoreBar } from "./shared";

const HAQ_CATEGORIES = [
  {
    label: "Dressing & Grooming",
    questions: [
      "Dress yourself, including tying shoelaces and doing buttons?",
      "Shampoo your hair?",
    ],
  },
  {
    label: "Arising",
    questions: [
      "Stand up from a straight chair?",
      "Get in and out of bed?",
    ],
  },
  {
    label: "Eating",
    questions: [
      "Cut your meat?",
      "Lift a full cup or glass to your mouth?",
      "Open a new milk carton?",
    ],
  },
  {
    label: "Walking",
    questions: [
      "Walk outdoors on flat ground?",
      "Climb up five steps?",
    ],
  },
  {
    label: "Hygiene",
    questions: [
      "Wash and dry your entire body?",
      "Take a tub bath?",
      "Get on and off the toilet?",
    ],
  },
  {
    label: "Reach",
    questions: [
      "Reach and get down a 5 lb (2 kg) object from just above your head?",
      "Bend down to pick up clothing from the floor?",
    ],
  },
  {
    label: "Grip",
    questions: [
      "Open car doors?",
      "Open jars that have been previously opened?",
      "Turn faucets on and off?",
    ],
  },
  {
    label: "Activities",
    questions: [
      "Run errands and shop?",
      "Get in and out of a car or public transport?",
      "Do chores such as vacuuming or light yard work?",
    ],
  },
];

const OPTIONS = [
  { label: "Without any difficulty", value: 0 },
  { label: "With some difficulty",   value: 1 },
  { label: "With much difficulty",   value: 2 },
  { label: "Unable to do",           value: 3 },
];

type CategoryScores = number[][];

const DEVICES_BY_CATEGORY = [
  ["Button hook/zipper pull", "Dressing aids"],
  ["Chair raisers", "Raised toilet seat"],
  ["Jar opener", "Enlarged utensil handle", "Non-slip mat"],
  ["Walking stick", "Walking frame", "Crutches"],
  ["Bath bench", "Grab bars", "Raised toilet seat"],
  ["Long-handled reacher"],
  ["Jar opener", "Splints", "Turntable"],
  ["Shopping trolley", "Wheelchair"],
];

export function FullHAQCalc() {
  const initialScores: CategoryScores = HAQ_CATEGORIES.map((c) =>
    Array(c.questions.length).fill(0)
  );
  const [scores, setScores] = useState<CategoryScores>(initialScores);
  const [aidsUsed, setAidsUsed] = useState<boolean[]>(Array(8).fill(false));

  const setQ = (catIdx: number, qIdx: number, val: number) => {
    setScores((prev) => {
      const next = prev.map((c) => [...c]);
      next[catIdx][qIdx] = val;
      return next;
    });
  };

  const toggleAid = (catIdx: number) => {
    setAidsUsed((prev) => {
      const next = [...prev];
      next[catIdx] = !next[catIdx];
      return next;
    });
  };

  const categoryScores = scores.map((qs, i) => {
    const maxQ = Math.max(...qs);
    return aidsUsed[i] ? Math.max(maxQ, 2) : maxQ;
  });

  const haqDI = categoryScores.reduce((s, v) => s + v, 0) / 8;

  const bands = [
    { label: "No/mild disability", max: 1.0,  colour: "text-emerald-700" },
    { label: "Moderate",           max: 2.0,  colour: "text-amber-600" },
    { label: "Severe",             max: 3.0,  colour: "text-red-600" },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">Full HAQ (HAQ-DI)</CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Badge variant="outline">RA / PsA / SSc</Badge>
            <Badge variant="outline">Patient-reported</Badge>
            <Badge variant="outline">20 items</Badge>
          </div>
        </div>
        <p className="text-xs text-slate-500">Stanford Health Assessment Questionnaire Disability Index — 20 questions across 8 functional domains. Mean category score (0–3). If aids/devices used, minimum score 2 for that category.</p>
      </CardHeader>
      <CardContent className="space-y-6">
        {HAQ_CATEGORIES.map((cat, catIdx) => (
          <div key={catIdx} className="border border-slate-100 rounded-xl p-4 bg-slate-50 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">{catIdx + 1}.</span>
                <p className="text-sm font-semibold text-slate-800">{cat.label}</p>
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${categoryScores[catIdx] === 0 ? "bg-emerald-100 text-emerald-700" : categoryScores[catIdx] <= 1 ? "bg-teal-100 text-teal-700" : categoryScores[catIdx] <= 2 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                  {categoryScores[catIdx].toFixed(0)}
                </span>
              </div>
              <label className="flex items-center gap-1.5 cursor-pointer text-xs text-slate-500 hover:text-slate-700">
                <input
                  type="checkbox"
                  checked={aidsUsed[catIdx]}
                  onChange={() => toggleAid(catIdx)}
                  className="h-3.5 w-3.5 rounded accent-teal-600"
                />
                Aids/devices used
              </label>
            </div>

            {aidsUsed[catIdx] && (
              <div className="text-xs text-teal-700 bg-teal-50 rounded px-2 py-1.5 border border-teal-100">
                Common aids: {DEVICES_BY_CATEGORY[catIdx].join(" · ")}
              </div>
            )}

            {cat.questions.map((q, qIdx) => (
              <div key={qIdx}>
                <p className="text-xs text-slate-600 mb-2">Are you able to: <em>{q}</em></p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                  {OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setQ(catIdx, qIdx, opt.value)}
                      className={`text-xs px-2 py-2 rounded-lg border transition-colors text-center ${
                        scores[catIdx][qIdx] === opt.value
                          ? "bg-teal-600 text-white border-teal-600"
                          : "bg-white text-slate-600 border-slate-200 hover:border-teal-300"
                      }`}
                    >
                      <span className="font-bold block">{opt.value}</span>
                      <span className="leading-tight">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}

        <div className="pt-2">
          <ScoreBar score={haqDI} bands={bands} />
          <div className="mt-2 bg-white rounded-xl border border-slate-200 p-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-700">HAQ-DI Score</p>
              <p className="text-2xl font-bold text-teal-700">{haqDI.toFixed(3)}</p>
            </div>
            <div className="grid grid-cols-8 gap-0.5 mt-2">
              {categoryScores.map((s, i) => (
                <div key={i} className="text-center">
                  <div className={`rounded text-xs py-0.5 font-bold ${s === 0 ? "bg-emerald-100 text-emerald-700" : s <= 1 ? "bg-teal-100 text-teal-600" : s <= 2 ? "bg-amber-100 text-amber-600" : "bg-red-100 text-red-600"}`}>
                    {s}
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5 truncate">{HAQ_CATEGORIES[i].label.split(" ")[0]}</p>
                </div>
              ))}
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-2">No/mild ≤1.0 · Moderate 1.0–2.0 · Severe 2.0–3.0 · Highest category question score used per domain · If aids/devices used, minimum score 2 for that category</p>
        </div>
      </CardContent>
    </Card>
  );
}
